import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OperationPlanDTO, PatchOperationPlanInput } from '@warplan/shared';
import { api } from './api-client.js';

export const OPERATION_PLAN_QUERY_KEY = ['operation-plan'] as const;

export function useOperationPlan() {
  return useQuery({
    queryKey: OPERATION_PLAN_QUERY_KEY,
    queryFn: () => api.get<OperationPlanDTO>('/api/operation-plan'),
    staleTime: 60_000,
  });
}

export function useUpdateOperationPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PatchOperationPlanInput) =>
      api.patch<OperationPlanDTO>('/api/operation-plan', patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: OPERATION_PLAN_QUERY_KEY });
      const previous = qc.getQueryData<OperationPlanDTO>(OPERATION_PLAN_QUERY_KEY);
      if (previous) {
        const next: OperationPlanDTO = { ...previous };
        if (patch.warNumber !== undefined) next.warNumber = patch.warNumber;
        if (patch.title !== undefined) next.title = patch.title;
        if (patch.date !== undefined) next.date = patch.date;
        if (patch.objective !== undefined) next.objective = patch.objective;
        if (patch.objectiveDefense !== undefined) next.objectiveDefense = patch.objectiveDefense;
        if (patch.objectiveType !== undefined) next.objectiveType = patch.objectiveType;
        if (patch.priorities !== undefined) next.priorities = [...patch.priorities];
        qc.setQueryData<OperationPlanDTO>(OPERATION_PLAN_QUERY_KEY, next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(OPERATION_PLAN_QUERY_KEY, ctx.previous);
    },
    onSuccess: (updated) => {
      qc.setQueryData<OperationPlanDTO>(OPERATION_PLAN_QUERY_KEY, updated);
    },
  });
}
